import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGesture } from 'react-use-gesture';
import { useTranslation } from 'react-i18next';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Fab from '@mui/material/Fab';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Slide, { SlideProps } from '@mui/material/Slide';
import Typography from '@mui/material/Typography';

import { useDecodedToken } from '../app/hooks';
import {
  useGetFeedOutingsQuery,
  useGetMyClubsQuery,
  useSubscribeTrainingMutation,
  useUnsubscribeTrainingMutation,
  useDeleteTrainingMutation,
} from '../api/apiSlice';
import { FeedTraining } from '../types/api';
import { useSportTypeLabel } from '../hooks/useSportTypeLabel';
import { groupByCalendarDay } from '../utils/dateGrouping';
import { sportMeta } from '../constants/sportMeta';

import CreateActivity from './createActivity';
import TrainingPhotosDialog from './TrainingPhotosDialog';
import ViewModeToggle, { ViewMode } from './ViewModeToggle';
import DayGroupHeader from './DayGroupHeader';
import FeedCard, { FeedCardMenuAction } from './FeedCard';
import FeedCardSkeleton from './FeedCardSkeleton';
import ActivityMapSlide from './ActivityMapSlide';
import ActivityWeatherSlide from './ActivityWeatherSlide';

// The stored MODE values map to the createActivity.modeOptions.* i18n keys.
const MODE_KEYS: Record<string, string> = {
  blablarun: 'blablarun',
  'avec pause': 'withBreak',
  'on fonce': 'fullSend',
};

// Open-Meteo's free forecast only reaches ~16 days out.
function daysUntil(startTime: string | null): number | null {
  if (!startTime) return null;
  const target = new Date(startTime.replace(' ', 'T')).getTime();
  if (isNaN(target)) return null;
  return Math.round((target - Date.now()) / 86_400_000);
}

const Alert = React.forwardRef<HTMLDivElement, React.ComponentProps<typeof MuiAlert>>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

// The DURATION column stores a datetime whose time-of-day *is* the duration
// (see createActivity) -- render it as a rough "~1 h 30" / "~45 min".
function durationLabel(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  const h = d.getHours();
  const m = d.getMinutes();
  if (h === 0 && m === 0) return null;
  if (h === 0) return `~${m} min`;
  return m === 0 ? `~${h} h` : `~${h} h ${String(m).padStart(2, '0')}`;
}

function Activities() {
  const { t, i18n } = useTranslation();
  const sportTypeLabel = useSportTypeLabel();
  const [isAddTrainingVisible, setAddTrainingVisibility] = useState(false);
  const [editingTraining, setEditingTraining] = useState<FeedTraining | null>(null);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [photosTrainingId, setPhotosTrainingId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('date');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if ((location.state as { justLoggedIn?: boolean } | null)?.justLoggedIn) {
      setAlertMessage(t('activities.welcomeBackToast'));
      setOpenAlert(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // Only ever meant to run once, right after a login redirect lands here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const decodedToken = useDecodedToken();
  const ID = decodedToken?.id ?? -1;

  const { data: activities = [], isLoading } = useGetFeedOutingsQuery(undefined, { skip: ID === -1 });
  const { data: myClubs = [] } = useGetMyClubsQuery();
  const [subscribeTraining] = useSubscribeTrainingMutation();
  const [unsubscribeTraining] = useUnsubscribeTrainingMutation();
  const [deleteTraining] = useDeleteTrainingMutation();

  // Anyone who is an admin or animator of at least one club can add an outing
  // (the create dialog picks which club); the feed itself is common across
  // every club the member belongs to.
  const canCreate = myClubs.some((club) => club.ROLE === 'ADMIN' || club.ROLE === 'ANIMATOR');

  const dayGroups = useMemo(() => {
    if (viewMode !== 'weekday') return [];
    const locale = i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return groupByCalendarDay(
      activities,
      (item) => (item.START_TIME ? new Date(item.START_TIME) : null),
      locale,
      t('common.unscheduled'),
    );
  }, [activities, viewMode, i18n.language, t]);

  const handleCloseAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setOpenAlert(false);
  };

  const handleRsvp = async (item: FeedTraining) => {
    try {
      if (item.is_participant) {
        await unsubscribeTraining({ userID: ID, trainingID: item.ID }).unwrap();
      } else {
        await subscribeTraining({ userID: ID, trainingID: item.ID }).unwrap();
      }
      setAlertMessage('');
      setOpenAlert(true);
    } catch (error) {
      console.error('Outing subscription failed:', error);
    }
  };

  const handleDeleteTraining = async (item: FeedTraining) => {
    if (!window.confirm(t('activities.deleteConfirm', { title: item.TITLE }))) return;
    try {
      await deleteTraining(item.ID).unwrap();
    } catch (error) {
      console.error('Outing delete failed:', error);
    }
  };

  const bind = useGesture({
    onDrag: ({ offset: [, offsetY] }: any) => {
      if (offsetY > 100) window.location.reload();
    },
  });

  const renderCard = (item: FeedTraining) => {
    const meta = sportMeta(item.TYPE);
    const modeKey = item.MODE ? MODE_KEYS[item.MODE] : undefined;
    const stats = [
      item.TYPE ? sportTypeLabel(item.TYPE) : null,
      item.DISTANCE != null ? `${item.DISTANCE} ${meta.distanceUnit}` : null,
      meta.showClimb && item.CLIMB != null ? `${item.CLIMB} m D+` : null,
      durationLabel(item.DURATION),
      modeKey ? t(`createActivity.modeOptions.${modeKey}`) : null,
    ].filter((s): s is string => Boolean(s));

    const by = t('activities.by', { name: `${item.FIRSTNAME} ${item.NAME}` });

    const canManage = myClubs.some(
      (club) => club.CLUB_ID === item.CLUB_ID && (club.ROLE === 'ADMIN' || club.ROLE === 'ANIMATOR'),
    );

    const menuActions: FeedCardMenuAction[] = canManage
      ? [
          {
            label: t('common.edit'),
            icon: <EditIcon fontSize="small" />,
            onClick: () => setEditingTraining(item),
          },
          {
            label: t('common.delete'),
            icon: <DeleteIcon fontSize="small" color="error" />,
            onClick: () => handleDeleteTraining(item),
          },
        ]
      : [];

    const hasCoords = Boolean(item.LATITUDE && item.LONGITUDE);
    const days = daysUntil(item.START_TIME);
    const extraMedia: React.ReactNode[] = [];
    if (hasCoords) {
      const lat = item.LATITUDE as string;
      const lng = item.LONGITUDE as string;
      extraMedia.push(
        <ActivityMapSlide
          key="map"
          lat={Number(lat)}
          lng={Number(lng)}
          label={[item.START_POINT, item.CITY].filter(Boolean).join(', ') || null}
        />,
      );
      if (item.START_TIME && days !== null && days >= 0 && days <= 16) {
        extraMedia.push(
          <ActivityWeatherSlide key="weather" lat={lat} lng={lng} startTime={item.START_TIME} />,
        );
      }
    }

    return (
      <Grid item key={item.ID} xs={12} sm={6} md={4}>
        <FeedCard
          image={item.IMAGE}
          clubName={item.CLUB_NAME}
          clubLogo={item.CLUB_LOGO}
          sportType={item.TYPE}
          date={item.START_TIME}
          title={item.TITLE}
          subtitle={item.START_POINT ? `${by} · ${item.START_POINT}` : by}
          stats={stats}
          extraMedia={extraMedia}
          menuActions={menuActions}
          participants={item.participants}
          participantLabel={(n) => t('feed.participantCount', { count: n })}
          isParticipant={Boolean(item.is_participant)}
          primaryAction={
            <>
              {item.is_participant ? (
                <Button size="small" color="error" variant="outlined" onClick={() => handleRsvp(item)}>
                  {t('activities.unsubscribe')}
                </Button>
              ) : (
                <Button size="small" variant="contained" onClick={() => handleRsvp(item)}>
                  {t('activities.subscribe')}
                </Button>
              )}
              <IconButton
                size="small"
                aria-label={t('trainingPhotos.title')}
                onClick={() => setPhotosTrainingId(item.ID)}
              >
                <PhotoCameraIcon fontSize="small" />
              </IconButton>
            </>
          }
        />
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 22 }} {...(bind() as any)}>
      {!isLoading && activities.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
        </Box>
      )}

      {viewMode === 'weekday' && !isLoading && activities.length > 0 ? (
        dayGroups.map((group) => (
          <div key={group.key}>
            <DayGroupHeader label={group.label} count={group.items.length} />
            <Grid container spacing={2}>
              {group.items.map(renderCard)}
            </Grid>
          </div>
        ))
      ) : (
        <Grid container spacing={2}>
          {isLoading &&
            Array.from({ length: 3 }).map((_, index) => (
              <Grid item key={index} xs={12} sm={6} md={4}>
                <FeedCardSkeleton />
              </Grid>
            ))}

          {!isLoading && activities.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                {t('activities.empty')}
              </Typography>
            </Grid>
          )}

          {!isLoading && activities.map(renderCard)}
        </Grid>
      )}

      <Snackbar
        open={openAlert}
        autoHideDuration={1500}
        onClose={handleCloseAlert}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseAlert} severity="success">
          {alertMessage}
        </Alert>
      </Snackbar>

      {(isAddTrainingVisible || editingTraining) && (
        <CreateActivity
          userID={ID}
          training={editingTraining ?? undefined}
          onClose={() => {
            setAddTrainingVisibility(false);
            setEditingTraining(null);
          }}
        />
      )}

      {photosTrainingId !== null && (
        <TrainingPhotosDialog trainingID={photosTrainingId} onClose={() => setPhotosTrainingId(null)} />
      )}

      {canCreate && (
        <Fab
          aria-label={t('activities.addOutingAction')}
          color="secondary"
          sx={{ position: 'fixed', bottom: 100, right: 20 }}
          onClick={() => setAddTrainingVisibility(true)}
        >
          <AddIcon />
        </Fab>
      )}
    </Container>
  );
}

export default Activities;
